// src/components/homeComponents/ConversionMachine.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaLock, FaShoppingCart, FaCheck } from "react-icons/fa";
import { FiRefreshCw, FiArrowLeft, FiArrowRight } from "react-icons/fi";

import w1 from "../../assets/websites/w1.png"; // Dot's Coffee
import w4 from "../../assets/websites/w4.png"; // Coolest Parfume
import w6 from "../../assets/websites/w6.png"; // Travelo
import web2 from "../../assets/websites/web2.png"; // Pro Interior TG
import w8 from "../../assets/websites/w8.png"; // Urban RenderX

gsap.registerPlugin(ScrollTrigger);

/*
  THE CONVERSION MACHINE — a pinned browser that sells while you scroll.
  Per project: the tab activates, the URL types itself, the site wipes in,
  a ghost cursor glides to the CTA and CLICKS it — ripple, toast,
  order counter +1. Websites that convert, demonstrated literally.
  NOTE for Amiri: swap urls/prices/labels for the real ones.
*/
const SITES = [
  {
    id: 0,
    name: "Dot's Coffee",
    url: "dotscoffee.com",
    image: w1,
    accent: "#b0764a",
    cta: "Order Coffee",
    toast: "New order — 2× Specialty Blend",
    price: "18.00€",
  },
  {
    id: 1,
    name: "Coolest Parfume",
    url: "coolestparfume.com",
    image: w4,
    accent: "#9281dd",
    cta: "Shop Fragrance",
    toast: "New order — Blonde Amber 50ml",
    price: "64.99€",
  },
  {
    id: 2,
    name: "Travelo",
    url: "travelo-ks.com",
    image: w6,
    accent: "#4ea9d6",
    cta: "Book Corfu",
    toast: "New booking — Corfu, 5 nights",
    price: "159.00€",
  },
  {
    id: 3,
    name: "Pro Interior",
    url: "prointerior-tg.com",
    image: web2,
    accent: "#b9a58c",
    cta: "Get a Quote",
    toast: "New lead — Living room project",
    price: "Lead",
  },
  {
    id: 4,
    name: "Urban RenderX",
    url: "urbanrenderx.com",
    image: w8,
    accent: "#e05656",
    cta: "See Renders",
    toast: "New inquiry — 3D visualization",
    price: "Lead",
  },
];

const ConversionMachine = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const browserRef = useRef(null);
  const urlRef = useRef(null);
  const cursorRef = useRef(null);
  const rippleRef = useRef(null);
  const toastRef = useRef(null);
  const toastTextRef = useRef(null);
  const toastPriceRef = useRef(null);
  const ordersRef = useRef(null);
  const imgRefs = useRef([]);
  const ctaRef = useRef(null);
  const demoTl = useRef(null);
  const orders = useRef(12);
  const lastIdx = useRef(-1);
  const [active, setActive] = useState(0);

  const playDemo = (i) => {
    const site = SITES[i];
    const urlNode = urlRef.current;
    const cursor = cursorRef.current;
    const ripple = rippleRef.current;
    const toast = toastRef.current;
    const cta = ctaRef.current;
    if (!urlNode || !cursor || !toast || !cta) return;

    if (demoTl.current) demoTl.current.kill();
    if (toastTextRef.current) toastTextRef.current.textContent = site.toast;
    if (toastPriceRef.current) toastPriceRef.current.textContent = site.price;

    const typing = { n: 0 };
    const tl = gsap.timeline();
    demoTl.current = tl;

    // 1 — url types itself
    tl.set(urlNode, { textContent: "" })
      .set(toast, { y: -70, opacity: 0 })
      .set(cursor, { left: "78%", top: "22%", scale: 1, opacity: 0 })
      .to(typing, {
        n: site.url.length,
        duration: 0.7,
        ease: "none",
        onUpdate: () => {
          urlNode.textContent = site.url.slice(0, Math.round(typing.n));
        },
      })
      // 2 — cursor appears and glides to the CTA
      .to(cursor, { opacity: 1, duration: 0.2 }, "+=0.15")
      .to(cursor, {
        left: "26%",
        top: "76%",
        duration: 0.9,
        ease: "power2.inOut",
      })
      // 3 — click: cursor press, CTA press, ripple
      .to(cursor, { scale: 0.75, duration: 0.1 })
      .to(cta, { scale: 0.94, duration: 0.1 }, "<")
      .to(cursor, { scale: 1, duration: 0.15 })
      .to(cta, { scale: 1, duration: 0.15 }, "<")
      .fromTo(
        ripple,
        { scale: 0, opacity: 0.6 },
        { scale: 3.4, opacity: 0, duration: 0.6, ease: "power2.out" },
        "<"
      )
      // 4 — the money moment: toast + order counter
      .to(toast, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: "back.out(2)",
        onStart: () => {
          orders.current += 1;
          if (ordersRef.current)
            ordersRef.current.textContent = orders.current;
        },
      })
      .to(cursor, { opacity: 0, duration: 0.3 }, "<+=0.2");
  };

  useEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    if (!section || !pin) return;

    let st;
    const t = setTimeout(() => {
      st = ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: `+=${SITES.length * 85}%`,
        pin: pin,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const p = self.progress;
          const idx = Math.min(
            SITES.length - 1,
            Math.round(p * (SITES.length - 1))
          );

          if (idx !== lastIdx.current) {
            lastIdx.current = idx;
            setActive(idx);

            // page wipe-in
            const img = imgRefs.current[idx];
            if (img) {
              gsap.fromTo(
                img,
                { clipPath: "inset(0 0 100% 0)", scale: 1.06 },
                {
                  clipPath: "inset(0 0 0% 0)",
                  scale: 1,
                  duration: 0.8,
                  ease: "power3.out",
                }
              );
            }

            // browser breathes with the swap
            if (browserRef.current) {
              gsap.fromTo(
                browserRef.current,
                { rotateX: 4, y: 12 },
                { rotateX: 0, y: 0, duration: 0.9, ease: "power3.out" }
              );
            }

            playDemo(idx);
          }
        },
      });
      // first demo on land
      ScrollTrigger.refresh();
    }, 120);

    return () => {
      clearTimeout(t);
      if (st) st.kill();
      if (demoTl.current) demoTl.current.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const site = SITES[active];

  return (
    <Section ref={sectionRef}>
      <Pin ref={pinRef}>
        <div
          className="aura"
          style={{
            background: `radial-gradient(circle at 50% 40%, ${site.accent}22 0%, #08040c 62%)`,
          }}
        />

        <div className="head">
          <span className="kicker">WEBSITES THAT SELL</span>
          <h2>
            Gorgeous is the <em>minimum.</em>
          </h2>
        </div>

        <Stage>
          <Browser ref={browserRef}>
            {/* chrome bar */}
            <div className="chrome">
              <div className="lights">
                <span className="r" />
                <span className="y" />
                <span className="g" />
              </div>
              <div className="tabs">
                {SITES.map((s, i) => (
                  <div
                    key={s.id}
                    className={`tab ${i === active ? "on" : ""}`}
                    style={{
                      "--accent": s.accent,
                    }}
                  >
                    <span className="fav" />
                    <span className="t-name">{s.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* url bar */}
            <div className="urlbar">
              <FiArrowLeft className="nav" />
              <FiArrowRight className="nav dim" />
              <FiRefreshCw className="nav" />
              <div className="field">
                <FaLock className="lock" />
                <span className="url" ref={urlRef}>
                  {SITES[0].url}
                </span>
                <span className="caret" />
              </div>
              <div className="orders">
                <FaShoppingCart />
                <b ref={ordersRef}>12</b>
              </div>
            </div>

            {/* viewport */}
            <div className="viewport">
              {SITES.map((s, i) => (
                <img
                  key={s.id}
                  ref={(el) => (imgRefs.current[i] = el)}
                  src={s.image}
                  alt={s.name}
                  style={{
                    zIndex: i === active ? 2 : 1,
                    opacity: i === active ? 1 : 0,
                  }}
                />
              ))}

              {/* our demo CTA the ghost cursor clicks */}
              <button
                className="cta"
                ref={ctaRef}
                style={{ background: site.accent }}
              >
                {site.cta}
                <span className="ripple" ref={rippleRef} />
              </button>

              {/* ghost cursor */}
              <div className="cursor" ref={cursorRef}>
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    d="M5 3 L19 12 L12 13.5 L9.5 20 Z"
                    fill="#ffffff"
                    stroke="#08040c"
                    strokeWidth="1.4"
                  />
                </svg>
              </div>

              {/* conversion toast */}
              <div className="toast" ref={toastRef}>
                <span className="check" style={{ background: site.accent }}>
                  <FaCheck />
                </span>
                <div className="toast-body">
                  <b ref={toastTextRef}>{SITES[0].toast}</b>
                  <span ref={toastPriceRef}>{SITES[0].price}</span>
                </div>
              </div>
            </div>
          </Browser>

          <Rail>
            {SITES.map((s, i) => (
              <div key={s.id} className={`stop ${i === active ? "on" : ""}`}>
                <span
                  className="bar"
                  style={{ background: i === active ? s.accent : undefined }}
                />
                <span className="label">{s.name}</span>
              </div>
            ))}
          </Rail>
        </Stage>

        <div className="foot">
          Every site above is live client work. The clicks are a preview of
          yours.
        </div>
      </Pin>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: #08040c;
`;

const Pin = styled.div`
  height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .aura {
    position: absolute;
    inset: 0;
    pointer-events: none;
    transition: background 0.8s ease;
  }

  .head {
    position: absolute;
    top: 6vh;
    left: 7%;
    z-index: 6;
    pointer-events: none;
    .kicker {
      font-family: "Montserrat";
      letter-spacing: 0.45em;
      font-size: 0.72rem;
      color: #836caf;
      font-weight: 500;
    }
    h2 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(1.8rem, 4vw, 3.6rem);
      line-height: 1;
      margin-top: 0.35em;
      em {
        font-style: italic;
        color: #9281dd;
      }
    }
  }

  .foot {
    position: absolute;
    bottom: 4vh;
    z-index: 6;
    color: #8a8595;
    font-size: 0.85rem;
    letter-spacing: 0.04em;
    text-align: center;
    padding: 0 7%;
  }
`;

const Stage = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3vw;
  width: 100%;
  perspective: 1400px;
`;

const Browser = styled.div`
  width: min(920px, 86vw);
  border-radius: 16px;
  background: #100c1a;
  border: 1px solid #2a2140;
  box-shadow: 0 60px 140px -50px #000, inset 0 1px 0 #ffffff0d;
  overflow: hidden;
  transform-style: preserve-3d;
  will-change: transform;

  .chrome {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    padding: 10px 14px 0;
    background: #0c0914;
    border-bottom: 1px solid #1d1530;
    .lights {
      display: flex;
      gap: 6px;
      padding-bottom: 10px;
      span {
        width: 11px;
        height: 11px;
        border-radius: 50%;
      }
      .r {
        background: #ff5f57;
      }
      .y {
        background: #febc2e;
      }
      .g {
        background: #28c840;
      }
    }
    .tabs {
      display: flex;
      gap: 4px;
      overflow: hidden;
      .tab {
        display: flex;
        align-items: center;
        gap: 7px;
        padding: 8px 14px;
        border-radius: 10px 10px 0 0;
        background: transparent;
        color: #6f6a7c;
        font-family: "Montserrat";
        font-size: 0.72rem;
        white-space: nowrap;
        transition: 0.35s;
        .fav {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #2c2342;
          transition: 0.35s;
        }
        &.on {
          background: #171126;
          color: #ffffff;
          .fav {
            background: var(--accent);
            box-shadow: 0 0 10px var(--accent);
          }
        }
      }
    }
  }

  .urlbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 14px;
    background: #171126;
    border-bottom: 1px solid #1d1530;
    .nav {
      color: #8a8595;
      font-size: 0.95rem;
      &.dim {
        opacity: 0.35;
      }
    }
    .field {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
      background: #0c0914;
      border: 1px solid #241c38;
      border-radius: 20px;
      padding: 6px 14px;
      .lock {
        color: #28c840;
        font-size: 0.7rem;
      }
      .url {
        font-family: "Montserrat";
        font-size: 0.78rem;
        color: #e6e2f0;
        letter-spacing: 0.02em;
      }
      .caret {
        width: 1px;
        height: 13px;
        background: #9281dd;
        animation: blink 0.9s steps(1) infinite;
      }
      @keyframes blink {
        50% {
          opacity: 0;
        }
      }
    }
    .orders {
      display: flex;
      align-items: center;
      gap: 7px;
      color: #b9c5fb;
      font-size: 0.8rem;
      background: #0c0914;
      border: 1px solid #241c38;
      border-radius: 20px;
      padding: 6px 12px;
      b {
        font-family: "Montserrat";
      }
    }
  }

  .viewport {
    position: relative;
    aspect-ratio: 16 / 9;
    background: #000;
    overflow: hidden;

    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      will-change: clip-path, transform;
      transition: opacity 0.3s ease;
    }

    .cta {
      position: absolute;
      left: 26%;
      top: 76%;
      transform: translate(-50%, -50%);
      z-index: 5;
      border: none;
      color: #0c0914;
      font-family: "Montserrat";
      font-weight: 700;
      font-size: clamp(0.7rem, 1.2vw, 0.95rem);
      padding: 12px 26px;
      border-radius: 30px;
      cursor: pointer;
      box-shadow: 0 14px 40px -10px #000000cc;
      overflow: visible;
      transition: background 0.5s ease;
      .ripple {
        position: absolute;
        inset: 0;
        border-radius: 30px;
        border: 2px solid #ffffff;
        opacity: 0;
        pointer-events: none;
      }
    }

    .cursor {
      position: absolute;
      z-index: 7;
      pointer-events: none;
      filter: drop-shadow(0 3px 8px #000000aa);
      will-change: left, top, transform;
    }

    .toast {
      position: absolute;
      top: 14px;
      right: 14px;
      z-index: 6;
      display: flex;
      align-items: center;
      gap: 10px;
      background: #0c0914ee;
      border: 1px solid #2a2140;
      border-radius: 14px;
      padding: 10px 16px 10px 10px;
      backdrop-filter: blur(8px);
      opacity: 0;
      transform: translateY(-70px);
      .check {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0c0914;
        font-size: 0.7rem;
        transition: background 0.5s ease;
      }
      .toast-body {
        display: flex;
        flex-direction: column;
        b {
          font-family: "Montserrat";
          font-size: 0.72rem;
          color: #fff;
          font-weight: 600;
        }
        span {
          font-family: "Montserrat";
          font-size: 0.68rem;
          color: #8a8595;
        }
      }
    }
  }

  @media (max-width: 700px) {
    .chrome .tabs .t-name {
      display: none;
    }
    .chrome .tabs .tab {
      padding: 8px 10px;
    }
    .urlbar .orders {
      display: none;
    }
    .viewport {
      .cta {
        padding: 9px 18px;
      }
      .toast {
        top: 8px;
        right: 8px;
        padding: 7px 10px 7px 7px;
        gap: 7px;
        .check {
          width: 24px;
          height: 24px;
          font-size: 0.58rem;
        }
        .toast-body b {
          font-size: 0.62rem;
        }
        .toast-body span {
          font-size: 0.58rem;
        }
      }
    }
  }
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .stop {
    display: flex;
    align-items: center;
    gap: 10px;
    .bar {
      width: 3px;
      height: 16px;
      border-radius: 3px;
      background: #2c2342;
      transition: 0.4s;
    }
    .label {
      font-family: "Montserrat";
      font-size: 0.7rem;
      color: #4f4a5c;
      letter-spacing: 0.06em;
      transition: 0.4s;
      white-space: nowrap;
    }
    &.on {
      .bar {
        height: 34px;
      }
      .label {
        color: #ffffff;
      }
    }
  }
  @media (max-width: 1100px) {
    display: none;
  }
`;

export default ConversionMachine;
